import React, { useMemo } from "react";
import {
  Avatar,
  Badge,
  Button,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import { SalonDocument, DocumentVersion } from "../../types/salonDocument";
import { documentListDisplayName } from "../../utils/clientDocumentUploadKey";
import {
  formatSalonDocumentVersionTimestamp,
  initialsFromStaffDisplayName,
  salonDocumentVersionDescriptionLine,
  salonDocumentVersionStaffDisplayName,
} from "../../utils/salonDocumentVersionUi";
import primaryAction from "../common/primaryFilledButton.module.css";
import styles from "./ClientDocumentsVersionsModal.module.css";

interface ClientDocumentsVersionsModalProps {
  opened: boolean;
  onClose: () => void;
  doc: SalonDocument | null;
  versions: DocumentVersion[];
  loading: boolean;
  readOnly: boolean;
  revertId: string | null;
  onRevert: (versionUuid: string) => void;
}

export default function ClientDocumentsVersionsModal({
  opened,
  onClose,
  doc,
  versions,
  loading,
  readOnly,
  revertId,
  onRevert,
}: ClientDocumentsVersionsModalProps) {
  const sortedVersions = useMemo(() => {
    return [...versions].sort((a, b) => {
      const byNum = (b.versionNumber ?? 0) - (a.versionNumber ?? 0);
      if (byNum !== 0) return byNum;
      const ta = a.createdDt ? new Date(a.createdDt).getTime() : 0;
      const tb = b.createdDt ? new Date(b.createdDt).getTime() : 0;
      return tb - ta;
    });
  }, [versions]);

  const docTitle = doc ? documentListDisplayName(doc) : "";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={4}>
          <Text fw={700} size="lg" component="span">
            Version History
          </Text>
          <Text size="xs" c="dimmed" component="span" fw={400}>
            {docTitle
              ? `Track changes and previous versions for “${docTitle}”.`
              : "Track changes and previous versions."}
          </Text>
        </Stack>
      }
      centered
      size="lg"
    >
      {loading ? (
        <Text c="dimmed" size="sm">
          Loading…
        </Text>
      ) : sortedVersions.length === 0 ? (
        <Text size="sm" c="dimmed">
          No version history returned.
        </Text>
      ) : (
        <ScrollArea
          type="auto"
          style={{ maxHeight: "min(560px, 65vh)" }}
          offsetScrollbars
        >
          <div className={styles.timeline}>
            <div className={styles.timelineLine} aria-hidden />
            {sortedVersions.map((v) => {
              const isCurrent =
                !!doc &&
                doc.currentVersionUuid != null &&
                v.versionUuid === doc.currentVersionUuid;
              const staffName = salonDocumentVersionStaffDisplayName(v);
              const description = salonDocumentVersionDescriptionLine(v);
              return (
                <div key={v.versionUuid} className={styles.timelineRow}>
                  <div className={styles.timelineRail}>
                    <span
                      className={isCurrent ? styles.dotCurrent : styles.dot}
                      aria-hidden
                    />
                  </div>
                  <Paper
                    withBorder
                    radius="md"
                    p="md"
                    className={
                      isCurrent
                        ? `${styles.card} ${styles.cardCurrent}`
                        : styles.card
                    }
                  >
                    {isCurrent ? (
                      <Badge
                        size="xs"
                        variant="filled"
                        color="teal"
                        className={styles.currentBadge}
                      >
                        Current
                      </Badge>
                    ) : null}
                    <Text
                      fw={700}
                      size="sm"
                      className={styles.cardTitle}
                      pr={isCurrent ? 72 : 0}
                    >
                      Version {v.versionNumber}
                    </Text>
                    <Text size="xs" c="dimmed" mt={4}>
                      {formatSalonDocumentVersionTimestamp(v.createdDt)}
                    </Text>
                    {description ? (
                      <Text size="sm" mt="xs" lineClamp={6}>
                        {description}
                      </Text>
                    ) : null}
                    {v.url ? (
                      <Text
                        component="a"
                        href={v.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="xs"
                        c="var(--brand-teal)"
                        className={styles.openLink}
                        display="block"
                      >
                        Open file
                      </Text>
                    ) : null}
                    <div className={styles.cardFooter}>
                      <Avatar
                        size={28}
                        radius="xl"
                        color="gray"
                        className={styles.avatar}
                      >
                        {initialsFromStaffDisplayName(staffName || "Unknown")}
                      </Avatar>
                      <Text
                        size="sm"
                        c="dark.7"
                        lineClamp={2}
                        style={{ flex: 1 }}
                      >
                        {staffName || "—"}
                      </Text>
                    </div>
                    {!readOnly && !isCurrent ? (
                      <Button
                        fullWidth
                        mt="sm"
                        size="xs"
                        variant="filled"
                        color="teal"
                        className={primaryAction.primaryFilled}
                        loading={revertId === v.versionUuid}
                        onClick={() => onRevert(v.versionUuid)}
                      >
                        Make current
                      </Button>
                    ) : null}
                  </Paper>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </Modal>
  );
}
